import { useState, useRef } from "react";
import { FiX, FiImage, FiType, FiSend, FiVideo, FiMusic } from "react-icons/fi";
import api from "../../utils/api";
import { useSocket } from "../../context/SocketContext";
import styles from "./StatusCreator.module.css";

const BG_COLORS = [
  "#7b7ff5", "#5b5bd6", "#ef4444", "#f97316",
  "#10b981", "#0ea5e9", "#8b5cf6", "#ec4899",
  "#1a1a2e", "#0e1621", "#2d1b69", "#064e3b",
];

const StatusCreator = ({ onClose, onCreated }) => {
  const socketRef = useSocket();
  const [mode, setMode]         = useState("text"); // text | image | video
  const [text, setText]         = useState("");
  const [bgColor, setBgColor]   = useState(BG_COLORS[0]);
  const [caption, setCaption]   = useState("");
  const [media, setMedia]       = useState(null);   // base64
  const [mediaType, setMediaType] = useState("image");
  const [loading, setLoading]   = useState(false);
  const [songUrl, setSongUrl]   = useState("");
  const [songTitle, setSongTitle] = useState("");
  const fileRef = useRef(null);
  const songFileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    setMediaType(isVideo ? "video" : "image");
    setMode(isVideo ? "video" : "image");
    const reader = new FileReader();
    reader.onload = () => setMedia(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSongFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSongTitle(file.name.replace(/\.[^.]+$/, ""));
    const reader = new FileReader();
    reader.onload = () => setSongUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (mode === "text" && !text.trim()) return;
    if ((mode === "image" || mode === "video") && !media) return;
    setLoading(true);
    try {
      const payload = {
        mediaType: mode === "text" ? "text" : mediaType,
        caption,
      };
      if (mode === "text") {
        payload.textContent = text.trim();
        payload.bgColor     = bgColor;
      } else {
        payload.media = media;
      }
      if (songUrl) {
        payload.songUrl   = songUrl;
        payload.songTitle = songTitle || "Music";
      }
      const res = await api.post("/status", payload);
      onCreated?.(res.data.status);
      socketRef?.current?.emit("status:new", { status: res.data.status });
      onClose();
    } catch {
    } finally { setLoading(false); }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h3>Add Status</h3>
          <button className={styles.closeBtn} onClick={onClose}><FiX size={20} /></button>
        </div>

        {/* Mode tabs */}
        <div className={styles.tabs}>
          <button className={`${styles.tab} ${mode === "text" ? styles.active : ""}`}
            onClick={() => setMode("text")}>
            <FiType size={15} /> Text
          </button>
          <button className={`${styles.tab} ${mode === "image" || mode === "video" ? styles.active : ""}`}
            onClick={() => fileRef.current?.click()}>
            <FiImage size={15} /> Photo/Video
          </button>
          <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={handleFile} />
          <input ref={songFileRef} type="file" accept="audio/*" hidden onChange={handleSongFile} />
        </div>

        {/* Preview */}
        <div className={styles.preview}>
          {mode === "text" && (
            <div className={styles.textPreview} style={{ background: bgColor }}>
              <textarea
                className={styles.textInput}
                placeholder="Type your status…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={200}
                autoFocus
              />
            </div>
          )}
          {mode === "image" && media && (
            <img src={media} alt="preview" className={styles.mediaPreview} />
          )}
          {mode === "video" && media && (
            <video src={media} className={styles.mediaPreview} controls />
          )}
          {(mode === "image" || mode === "video") && !media && (
            <div className={styles.emptyMedia} onClick={() => fileRef.current?.click()}>
              <FiImage size={48} />
              <p>Click to select photo or video</p>
            </div>
          )}
        </div>

        {/* Color picker (text mode) */}
        {mode === "text" && (
          <div className={styles.colorPicker}>
            {BG_COLORS.map((c) => (
              <button
                key={c}
                className={`${styles.colorBtn} ${bgColor === c ? styles.colorActive : ""}`}
                style={{ background: c }}
                onClick={() => setBgColor(c)}
              />
            ))}
          </div>
        )}

        {/* Caption (media mode) */}
        {(mode === "image" || mode === "video") && media && (
          <input
            className={styles.captionInput}
            placeholder="Add a caption…"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={200}
          />
        )}

        {/* Song attachment */}
        <div className={styles.songSection}>
          {songUrl ? (
            <div className={styles.songAttached}>
              <div className={styles.songIcon}><FiMusic size={15} /></div>
              <span className={styles.songName}>{songTitle || "Music"}</span>
              <button className={styles.songRemove} onClick={() => { setSongUrl(""); setSongTitle(""); }} title="Remove song">
                <FiX size={14} />
              </button>
            </div>
          ) : (
            <button className={styles.songBtn} onClick={() => songFileRef.current?.click()}>
              <FiMusic size={14} />
              Add music to status
            </button>
          )}
        </div>

        {/* Submit */}
        <button
          className={styles.submitBtn}
          onClick={handleSubmit}
          disabled={loading || (mode === "text" && !text.trim()) || ((mode === "image" || mode === "video") && !media)}
        >
          {loading ? "Posting…" : <><FiSend size={16} /> Post Status</>}
        </button>
      </div>
    </div>
  );
};

export default StatusCreator;
